import React from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import { Button } from '~/components/_atoms'
import { Warning } from '~/components/icons/YnsIcon'

const ConnectionLost = ({
  deloitteUser = false,
  disconnectionReason = 'unknown',
  onRetry = null,
  onEndChat = null,
  exitUrl = null,
}) => {
  const { t } = useTranslation('coach_chat')

  const isExitOnly = !!exitUrl

  // When exitUrl is set, use a single message for "connection lost, please try again"
  const getExitOnlyMessage = () => {
    return deloitteUser
      ? t(
          'connection_lost.exit_only_message_deloitte',
          'We are sorry, but it looks like the connection with your advisor has been lost. Please exit the chat and try again later.',
        )
      : t(
          'connection_lost.exit_only_message',
          'We are sorry, but it looks like the connection with your coach has been lost. Please exit the chat and try again later.',
        )
  }

  // Different messages based on disconnection reason (when not exit-only)
  const getMessage = () => {
    if (isExitOnly) {
      return getExitOnlyMessage()
    }
    switch (disconnectionReason) {
      case 'coach_left_room':
        return deloitteUser
          ? t(
              'connection_lost.coach_left_deloitte',
              'Your advisor has left the session.',
            )
          : t('connection_lost.coach_left', 'Your coach has left the session.')
      case 'peer_connection_lost':
        return deloitteUser
          ? t(
              'connection_lost.peer_disconnected_deloitte',
              'Connection with your advisor was lost.',
            )
          : t(
              'connection_lost.peer_disconnected',
              'Connection with your coach was lost.',
            )
      case 'network_issue':
        return t(
          'connection_lost.network',
          'Network connection lost. Attempting to reconnect...',
        )
      case 'heartbeat_failed':
        return deloitteUser
          ? t(
              'connection_lost.heartbeat_failed_deloitte',
              'Lost connection with your advisor.',
            )
          : t(
              'connection_lost.heartbeat_failed',
              'Lost connection with your coach.',
            )
      default:
        return deloitteUser
          ? t(
              'connection_lost.generic_deloitte',
              'Connection lost. Attempting to reconnect...',
            )
          : t(
              'connection_lost.generic',
              'Connection lost. Attempting to reconnect...',
            )
    }
  }

  // Determine if we should show retry button (hidden in exit-only mode)
  const shouldShowRetry = () => {
    if (isExitOnly) return false
    return (
      onRetry &&
      (disconnectionReason === 'network_issue' ||
        disconnectionReason === 'peer_connection_lost')
    )
  }

  // Determine if we should show end chat button (hidden in exit-only mode)
  const shouldShowEndChat = () => {
    if (isExitOnly) return false
    return (
      onEndChat &&
      (disconnectionReason === 'coach_left_room' ||
        disconnectionReason === 'heartbeat_failed')
    )
  }

  const handleOkExit = () => {
    if (exitUrl) {
      window.top.location.href = exitUrl
    }
  }

  const handleRetry = () => {
    if (onRetry) {
      onRetry()
    }
  }

  const handleEndChat = () => {
    if (onEndChat) {
      onEndChat()
    }
  }

  return (
    <div className="connection-lost-container flex h-full w-full items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        {/* Main connection lost banner */}
        <div className="connection-lost-banner flex items-center gap-2 rounded-lg bg-[#FFB800] px-4 py-3">
          {/* Warning icon */}
          <Warning className="h-6 w-6 flex-shrink-0 text-black" />

          {/* Message text */}
          <div className="connection-message flex flex-col justify-center break-words font-roboto text-lg font-normal text-black">
            {getMessage()}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          {isExitOnly ? (
            <Button
              onClick={handleOkExit}
              variant="solid"
              color="secondary"
              className="px-6 py-2"
            >
              {t('connection_lost.ok_button', 'OK')}
            </Button>
          ) : (
            <>
              {shouldShowRetry() && (
                <Button
                  onClick={handleRetry}
                  variant="solid"
                  color="primary"
                  className="px-6 py-2"
                >
                  {t('connection_lost.retry_button', 'Retry Connection')}
                </Button>
              )}

              {shouldShowEndChat() && (
                <Button
                  onClick={handleEndChat}
                  variant="outline"
                  color="secondary"
                  className="px-6 py-2"
                >
                  {deloitteUser
                    ? t('connection_lost.end_session_deloitte', 'End Session')
                    : t('connection_lost.end_chat', 'End Chat')}
                </Button>
              )}
            </>
          )}
        </div>

        {/* Additional info for certain disconnection types (hidden in exit-only mode) */}
        {!isExitOnly && disconnectionReason === 'coach_left_room' && (
          <div className="max-w-md text-center text-sm text-gray-600">
            {deloitteUser
              ? t(
                  'connection_lost.coach_left_info_deloitte',
                  'Your advisor has ended the session. You can start a new session with another advisor.',
                )
              : t(
                  'connection_lost.coach_left_info',
                  'Your coach has ended the session. You can start a new session with another coach.',
                )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ConnectionLost
